/**
 * VendorDetailOverlay — Full-screen overlay for vendor/product detail from MarketView.
 *
 * Shows overall scores, technical scorecard performance,
 * compliance info, key pros/risks, and an action pane.
 *
 * Triggered by clicking a ProductResultCard in MarketView.
 * Uses a portal to render over the MarketView content.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
  X, ExternalLink, Building2, Shield, AlertTriangle,
  ChevronDown, ChevronRight, ChevronLeft, Play, Pause, FileText,
  Star, CheckCircle, XCircle, Bookmark, BookmarkCheck,
  User, Mail, Phone, Send, Copy, Brain,
} from 'lucide-react'
import { createPortal } from 'react-dom'
import ReactMarkdown from 'react-markdown'
import { MarketViewSearchResult } from '../services/api'
import ScoreRadarChart from './marketview/ScoreRadarChart'
import {
  agentOverallValue,
  agentScoreValue,
  describeOverall,
  describeScore,
  overallBadgeClasses,
} from '../lib/scoreDisplay'
import Badge from './ui/Badge'
import Button from './ui/Button'
import Modal from './ui/Modal'
import VendorContractsModal from './marketview/VendorContractsModal'
import { tokenManager } from '../lib/api'
import { useViewportContext } from '../hooks/useViewportContext'
import { useAgent } from '../contexts/AgentContext'
import { trackEvent } from '../services/tracking'
import { useViewTracking, useModalTracking } from '../hooks/useViewTracking'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

// ── Types ──────────────────────────────────────────────────────────

interface TeamMember {
  id: number
  name: string
  role: string
  title: string
  email: string
  phone: string
  linkedin_url: string
  is_primary_contact: boolean
  is_founder: boolean
  is_c_suite: boolean
}

interface VendorDetailData {
  description?: string
  team_members?: TeamMember[]
  primary_contact_email?: string
  sam_gov_id?: string
  uei?: string
  sam_status?: string
  cmmc_level?: string
  iso_certifications?: string[]
  cage_code?: string
  duns_number?: string
  naics_codes?: string[]
  founded_year?: number
  employee_count?: number
  city?: string
  state?: string
  country?: string
}

interface ScorecardEvaluation {
  id: number
  scorecard_name: string
  scorecard_type: string
  overall_score: number
  completeness_percentage: number
  items_with_data: number
  total_items: number
  overall_assessment?: string
  key_strengths?: string[]
  major_gaps?: string[]
  evaluation_mode?: string
  section_evaluations: SectionEvaluation[]
}

interface SectionEvaluation {
  id: number
  section: { id: number; title: string }
  score: number
  items_with_data: number
  total_items: number
  item_evaluations: ItemEvaluation[]
}

interface ItemEvaluation {
  id: number
  item: number
  item_title: string
  spec_name: string | null
  score: number
  rationale: string
  has_data: boolean
  item_details: {
    title: string
    description: string
    importance: string
    spec: { name: string; unit: string; spec_type: string } | null
  } | null
  acceptance_criteria: {
    type: string
    unit?: string
    higher_is_better?: boolean
    lower_threshold?: number
    upper_threshold?: number
  } | null
  observed_value: {
    type: string
    value: number | boolean | string
    unit?: string
    confidence: string
    source: string
    rationale: string
    commentary?: string
    source_url?: string
    notes?: string
  } | null
}

interface QualitativeObservation {
  id: number
  spec_name: string
  spec_priority: number
  value_text: string
  confidence: number
  observed_date: string
  source_type: string
}

interface VendorDetailOverlayProps {
  result: MarketViewSearchResult
  programId?: string
  onClose: () => void
  allResults?: MarketViewSearchResult[]
  currentIndex?: number
  onNavigate?: (index: number) => void
  /** Fired once a shortlist toggle has settled against the server, so a parent
   *  that re-reads shortlist state doesn't race the round-trip. */
  onShortlistPersisted?: () => void
}

// ── Helpers ────────────────────────────────────────────────────────

/**
 * Whether the vendor record carries anything the compliance grid can show.
 *
 * A successful fetch always produces a VendorDetailData object, so the object
 * being non-null says the call worked, not that the vendor has any filings.
 * Without this the empty state was unreachable and "Compliance data not
 * available." only ever appeared on a failed call (DEV-672).
 */
const hasComplianceData = (detail: VendorDetailData): boolean =>
  Boolean(
    detail.sam_gov_id ||
    detail.sam_status ||
    detail.uei ||
    detail.cmmc_level ||
    detail.cage_code ||
    (detail.iso_certifications && detail.iso_certifications.length > 0)
  )

const getScoreColor = (value: number) => {
  if (value >= 80) return 'text-success'
  if (value >= 60) return 'text-primary'
  if (value >= 40) return 'text-warning'
  return 'text-destructive'
}

// ── Component ──────────────────────────────────────────────────────

const VendorDetailOverlay: React.FC<VendorDetailOverlayProps> = ({
  result,
  programId,
  onClose,
  allResults = [],
  currentIndex = 0,
  onNavigate,
  onShortlistPersisted,
}) => {
  const { product, vendor, scores, semantic_score } = result
  const overallScore = describeOverall(scores)
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 768px)').matches : true
  )
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)')
    const handler = () => setIsDesktop(mq.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])
  const [vendorDetail, setVendorDetail] = useState<VendorDetailData | null>(null)
  const [vendorDetailError, setVendorDetailError] = useState<string | null>(null)
  const [evaluations, setEvaluations] = useState<ScorecardEvaluation[]>([])
  const [loadingDetail, setLoadingDetail] = useState(true)
  const [isShortlisted, setIsShortlisted] = useState(false)
  const [shortlistLoading, setShortlistLoading] = useState(false)
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['scores', 'qualitative', 'compliance', 'pros_risks'])
  )
  
  // Modal state
  const [showContactModal, setShowContactModal] = useState(false)
  const [showRequestModal, setShowRequestModal] = useState(false)
  const [showContractsModal, setShowContractsModal] = useState(false)
  const [programName, setProgramName] = useState<string>('')
  const [customInfoRequest, setCustomInfoRequest] = useState('')

  // Qualitative analysis observations
  const [qualitativeObs, setQualitativeObs] = useState<QualitativeObservation[]>([])
  const [expandedQualItems, setExpandedQualItems] = useState<Set<number>>(new Set())

  // ── Event tracking ──
  const trackingProps = { vendor_id: vendor.id, product_id: product.id, program_id: programId }
  const { onClose: trackedClose } = useViewTracking('vendor_detail', onClose, trackingProps)
  const contactTracking = useModalTracking('contact_info', { vendor_id: vendor.id })
  const requestInfoTracking = useModalTracking('request_info', { vendor_id: vendor.id, program_id: programId })
  const contractsTracking = useModalTracking('vendor_contracts', { vendor_id: vendor.id })

  // Evaluation state
  const [evaluationLoading, setEvaluationLoading] = useState(false)
  const [evaluationError, setEvaluationError] = useState<string | null>(null)

  // Tier 2: Register viewport serializer for agent awareness
  useViewportContext('vendor_overlay', () => ({
    product: {
      id: product.id,
      name: product.name,
      description: product.description?.slice(0, 300),
    },
    vendor: {
      id: vendor.id,
      name: vendor.name,
      is_small_business: vendor.is_small_business,
      is_8a_certified: vendor.is_8a_certified,
    },
    // The agent gets the state string, not the placeholder behind it, so it
    // cannot report an unmeasured dimension as a 50 (DEV-584).
    scores: {
      overall: agentOverallValue(scores),
      need_fit: agentScoreValue(scores.need_fit),
      technical: agentScoreValue(scores.technical),
      manufacturing: agentScoreValue(scores.manufacturing),
      vc_metrics: agentScoreValue(scores.vc_metrics),
      dod_partnership: agentScoreValue(scores.dod_partnership),
    },
    semantic_score,
    compliance: vendorDetail ? {
      sam_status: vendorDetail.sam_status,
      cmmc_level: vendorDetail.cmmc_level,
      cage_code: vendorDetail.cage_code,
    } : null,
    // A failed detail fetch and a vendor with no compliance record both leave
    // `compliance` null, so name the failure rather than let the agent report
    // "no compliance data" for a call that never succeeded (DEV-672).
    compliance_error: vendorDetailError,
    is_shortlisted: isShortlisted,
    program_id: programId,
  }), [product, vendor, scores, semantic_score, vendorDetail, vendorDetailError, isShortlisted, programId])

  // Fetch program name
  useEffect(() => {
    if (programId) {
      const token = tokenManager.getAccessToken()
      fetch(`${API_BASE_URL}/api/programs/${programId}/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data) setProgramName(data.name)
        })
        .catch(err => console.error('Failed to load program name:', err))
    }
  }, [programId])

  // Reset shortlist state immediately when the viewed product/vendor changes
  useEffect(() => {
    setIsShortlisted(false)
  }, [product.id, vendor.id])

  // Load vendor detail and scorecard evaluations
  useEffect(() => {
    // Arrowing between results swaps the props on a mounted overlay, so a slow
    // response for the vendor the user just left would otherwise land on top of
    // the one they are looking at — the same "A's data under B's name" symptom
    // as the missing reset, arriving by a different route (DEV-672).
    let cancelled = false

    const loadDetails = async () => {
      setLoadingDetail(true)
      // The overlay stays mounted while the user arrows between results, so
      // anything left over belongs to the previous vendor. Without this the
      // drawer rendered vendor A's location, blurb and compliance under vendor
      // B's name, permanently if B's own call then failed (DEV-672).
      setVendorDetail(null)
      setVendorDetailError(null)
      const token = tokenManager.getAccessToken()

      try {
        // Vendor detail. Every non-2xx used to fall through to the same
        // "Compliance data not available." as an empty record, so a denial, a
        // 5xx and a dead network were indistinguishable (DEV-672). Same
        // branching DEV-663 applied in VendorContractsModal.
        const vRes = await fetch(`${API_BASE_URL}/api/vendors/${vendor.id}/`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (cancelled) return
        if (vRes.ok) {
          const vData = await vRes.json()
          if (cancelled) return
          setVendorDetail({
            description: vData.description,
            team_members: vData.team_members || [],
            primary_contact_email: vData.primary_contact_email,
            sam_gov_id: vData.sam_gov_id,
            uei: vData.uei,
            sam_status: vData.sam_status,
            cmmc_level: vData.cmmc_level,
            iso_certifications: vData.iso_certifications || [],
            cage_code: vData.cage_code,
            duns_number: vData.duns_number,
            naics_codes: vData.naics_codes || [],
            founded_year: vData.founded_year,
            employee_count: vData.employee_count,
            city: vData.city,
            state: vData.state,
            country: vData.country,
          })
        } else if (vRes.status === 401) {
          // The global fetch interceptor already refreshes and replays a 401,
          // and clears the session and routes to / (the login page) when the refresh
          // itself fails (DEV-662, PR #333). A 401 arriving here survived
          // that, so report it rather than start a second refresh path.
          setVendorDetailError('Your session has expired. Sign in again to load this vendor.')
        } else if (vRes.status === 403 || vRes.status === 404) {
          // Not "no compliance record": the vendor itself is not readable by
          // this user. VendorViewSet filters get_object() through the
          // visibility queryset, so a denied vendor 404s exactly like a
          // missing one (DEV-669).
          setVendorDetailError('This vendor is not available to your account.')
        } else {
          setVendorDetailError(`Failed to load vendor details (HTTP ${vRes.status}).`)
        }

        // Scorecard evaluations for this product + vendor
        // Technical evals are product-level; manufacturing/vc/dod are vendor-level
        try {
          let evalUrl = `${API_BASE_URL}/api/scorecard-evaluations/?product=${product.id}&vendor=${vendor.id}&deduplicate=true`
          if (programId) evalUrl += `&program=${programId}`
          const eRes = await fetch(evalUrl, {
            headers: { Authorization: `Bearer ${token}` },
          })
          if (eRes.ok) {
            const eData = await eRes.json()
            if (cancelled) return
            setEvaluations(eData.results || eData || [])
          }
        } catch {
          // Evaluations may not exist yet
        }

        // Qualitative (text) observations for this product
        try {
          const qRes = await fetch(
            `${API_BASE_URL}/api/observations/?product=${product.id}&spec_type=text`,
            { headers: { Authorization: `Bearer ${token}` } }
          )
          if (qRes.ok) {
            const qData = await qRes.json()
            if (cancelled) return
            const obs: QualitativeObservation[] = (qData.results || qData || [])
              .filter((o: any) => o.value_text && o.value_text.trim())
              .sort((a: any, b: any) => (b.spec_priority || 3) - (a.spec_priority || 3))
            setQualitativeObs(obs)
            // Auto-expand the first item
            if (obs.length > 0) {
              setExpandedQualItems(new Set([obs[0].id]))
            }
          }
        } catch {
          // Qualitative observations optional
        }

        // Check if vendor is already shortlisted for this program
        if (cancelled) return
        setIsShortlisted(false)
        if (programId) {
          try {
            const slRes = await fetch(
              `${API_BASE_URL}/api/programs/${programId}/vendor-recommendations/?limit=100`,
              { headers: { Authorization: `Bearer ${token}` } },
            )
            if (slRes.ok) {
              const slData = await slRes.json()
              if (cancelled) return
              const recs = slData.recommendations || slData.results || []
              const match = recs.some(
                (r: any) =>
                  (r.vendor_id === vendor.id || r.vendor?.id === vendor.id) &&
                  (r.product_id === product.id || r.product?.id === product.id)
              )
              setIsShortlisted(match)
            }
          } catch {
            // Shortlist check optional
          }
        }
      } catch (err) {
        // The evaluation, observation and shortlist calls below each swallow
        // their own failures, so a throw reaching here is the vendor-detail
        // call: a dead network or an unparseable body, not an empty record.
        console.error('Failed to load vendor details:', err)
        if (!cancelled) {
          setVendorDetailError('Could not reach the server to load vendor details.')
        }
      } finally {
        if (!cancelled) setLoadingDetail(false)
      }
    }

    loadDetails()
    return () => {
      cancelled = true
    }
  }, [vendor.id, product.id])

  // Auto-trigger full evaluation on overlay open
  useEffect(() => {
    if (!product.id || !programId || loadingDetail) return
    // Run unless every dimension already has an item-level ('complete')
    // evaluation. A dimension still on the MarketView fast eval has only
    // section rollups, so we (re)run to populate its line-by-line items.
    if (!isFullyEvaluated && !evaluationLoading && !evaluationError) {
      handleRunFullEvaluation()
    }
  }, [product.id, programId, loadingDetail])

  // Toggle shortlist via backend
  const handleShortlistToggle = useCallback(async () => {
    if (!programId) return
    setShortlistLoading(true)
    const token = tokenManager.getAccessToken()
    try {
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/shortlist/`, {
        method: isShortlisted ? 'DELETE' : 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          vendor_id: vendor.id,
          product_id: product.id,
        }),
      })
      if (res.ok) {
        trackEvent(isShortlisted ? 'vendor_unshortlisted' : 'vendor_shortlisted', {
          vendor_id: vendor.id, product_id: product.id, program_id: programId,
        })
        setIsShortlisted(!isShortlisted)
      }
    } catch (err) {
      console.error('Failed to toggle shortlist:', err)
    } finally {
      setShortlistLoading(false)
      onShortlistPersisted?.()
    }
  }, [programId, vendor.id, product.id, isShortlisted, onShortlistPersisted])

  // Keyboard shortcuts: Escape to close, Arrow keys to navigate, S to shortlist
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input/textarea
      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        return
      }

      // Close on Escape
      if (e.key === 'Escape') {
        trackedClose()
        return
      }

      // Toggle shortlist on S
      if ((e.key === 's' || e.key === 'S') && programId) {
        e.preventDefault()
        handleShortlistToggle()
        return
      }

      // Navigate on Arrow keys (if navigation is enabled)
      if (onNavigate && allResults.length > 1) {
        if (e.key === 'ArrowLeft' && currentIndex > 0) {
          e.preventDefault()
          onNavigate(currentIndex - 1)
        } else if (e.key === 'ArrowRight' && currentIndex < allResults.length - 1) {
          e.preventDefault()
          onNavigate(currentIndex + 1)
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [trackedClose, onNavigate, allResults.length, currentIndex, programId, handleShortlistToggle])

  // Run full evaluation across all scorecard types
  const handleRunFullEvaluation = useCallback(async (force = false) => {
    if (!product.id || !programId) return
    trackEvent('full_evaluation_triggered', {
      vendor_id: vendor.id, product_id: product.id, program_id: programId,
    })

    setEvaluationLoading(true)
    setEvaluationError(null)
    const token = tokenManager.getAccessToken()

    try {
      const res = await fetch(`${API_BASE_URL}/api/scorecard-evaluations/evaluate/`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          product_id: product.id,
          vendor_id: vendor.id,
          program_id: programId,
          // Default open: reuse the cached evaluation. The explicit button
          // passes force=true to recompute (and re-run the LLM narrative).
          force,
        }),
      })

      if (res.ok) {
        const data = await res.json()
        const newEvaluations: ScorecardEvaluation[] = data.evaluations || []
        // Replace evaluations of matching scorecard_type, keep the rest
        setEvaluations(prev => {
          const byType = new Map(prev.map(e => [e.scorecard_type, e]))
          for (const e of newEvaluations) {
            byType.set(e.scorecard_type, e)
          }
          return Array.from(byType.values())
        })
      } else {
        // DRF reports permission and auth failures under `detail`, and this
        // view's own validation errors under `error`.
        const errorData = await res.json().catch(() => ({}))
        setEvaluationError(
          errorData.error || errorData.detail || `Evaluation failed (${res.status})`
        )
      }
    } catch (err) {
      console.error('Failed to run full evaluation:', err)
      setEvaluationError('Failed to run evaluation. Please try again.')
    } finally {
      setEvaluationLoading(false)
    }
  }, [product.id, vendor.id, programId])

  // Deduplicate evaluations: keep only the most recent per scorecard_type
  const dedupedEvaluations = useMemo(() => {
    const byType = new Map<string, ScorecardEvaluation>()
    for (const ev of evaluations) {
      const key = ev.scorecard_type || ev.scorecard_name
      const existing = byType.get(key)
      // Keep the last one encountered (API returns most recent first)
      if (!existing) {
        byType.set(key, ev)
      }
    }
    return Array.from(byType.values())
  }, [evaluations])

  // True only when EVERY dimension that has an evaluation has been scored at the
  // item level ('complete' mode). The MarketView fast path persists section
  // rollups but no per-item rows, so a dimension sitting on a fast eval (e.g.
  // Basic Requirements scored only during search) has nothing to show line-by-
  // line until it's upgraded. Gating on "every" rather than "some" ensures a
  // program where one dimension is already complete still gets the rest filled in.
  const isFullyEvaluated = useMemo(
    () =>
      dedupedEvaluations.length > 0 &&
      dedupedEvaluations.every((e) => e.evaluation_mode === 'complete'),
    [dedupedEvaluations],
  )

  // Auto-expand evaluation sections when they load
  useEffect(() => {
    if (dedupedEvaluations.length > 0) {
      setExpandedSections((prev) => {
        const next = new Set(prev)
        dedupedEvaluations.forEach((e) => {
          if (e.scorecard_type) next.add(`score-${e.scorecard_type}`)
        })
        return next
      })
    }
  }, [dedupedEvaluations])

  const toggleSection = (key: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const SectionHeader: React.FC<{
    id: string
    icon: React.ReactNode
    title: string
    badge?: React.ReactNode
  }> = ({ id, icon, title, badge }) => (
    <button
      onClick={() => toggleSection(id)}
      className="w-full flex items-center gap-2 px-6 py-3 border-b border-border hover:bg-muted/30 transition-colors"
    >
      {expandedSections.has(id) ? (
        <ChevronDown className="w-4 h-4 text-muted-foreground" />
      ) : (
        <ChevronRight className="w-4 h-4 text-muted-foreground" />
      )}
      {icon}
      <span className="text-sm font-semibold text-foreground">{title}</span>
      {badge}
    </button>
  )

  // Product image carousel
  const allImages = useMemo(() => {
    const imgs = (product.images || []).map(img => img.image_url).filter(Boolean)
    if (imgs.length === 0 && (product.primary_image?.image_url || product.image_url)) {
      imgs.push(product.primary_image?.image_url || product.image_url)
    }
    return imgs
  }, [product.images, product.primary_image, product.image_url])

  const [currentImageIdx, setCurrentImageIdx] = useState(0)
  const [autoPlay, setAutoPlay] = useState(true)
  const [isHovering, setIsHovering] = useState(false)

  // Auto-advance every 5 seconds
  useEffect(() => {
    if (!autoPlay || allImages.length <= 1) return
    const timer = setInterval(() => {
      setCurrentImageIdx(prev => (prev + 1) % allImages.length)
    }, 5000)
    return () => clearInterval(timer)
  }, [autoPlay, allImages.length])

  const goToPrev = useCallback(() => {
    setCurrentImageIdx(prev => {
      const next = (prev - 1 + allImages.length) % allImages.length
      trackEvent('image_scrolled', { vendor_id: vendor.id, product_id: product.id, image_index: next, navigation_method: 'prev' })
      return next
    })
  }, [allImages.length, vendor.id, product.id])

  const goToNext = useCallback(() => {
    setCurrentImageIdx(prev => {
      const next = (prev + 1) % allImages.length
      trackEvent('image_scrolled', { vendor_id: vendor.id, product_id: product.id, image_index: next, navigation_method: 'next' })
      return next
    })
  }, [allImages.length, vendor.id, product.id])

  const { isOpen: agentOpen } = useAgent()

  const overlayContent = (
    <div data-testid="vendor-detail-overlay" className="fixed inset-0 z-30">
      {/* Backdrop — heavy blur + light scrim for contrast against dark UI */}
      <div
        className="absolute inset-0 bg-white/10 backdrop-blur-2xl"
        onClick={trackedClose}
      />

      {/* Panel — full bleed on mobile, centered on md+; respects agent drawer */}
      <div
        className="absolute top-[4rem] sm:top-[calc(4rem+2.5%)] bottom-0 sm:bottom-[2.5%] left-0 sm:left-[5%] right-0 sm:right-[5%] md:right-[unset]
                   rounded-none sm:rounded-2xl border-0 sm:border border-border/60
                   bg-background/98 shadow-2xl flex flex-col overflow-hidden
                   transition-[right] duration-300 ease-in-out"
        style={isDesktop ? { right: agentOpen ? 'calc(24rem + 2.5%)' : '5%' } : undefined}
      >
        {/* Header */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-4 px-4 sm:px-6 py-3 sm:py-4 border-b border-border shrink-0">
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={trackedClose}
              className="p-1.5 rounded-lg hover:bg-muted transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            {allResults.length > 1 && (
              <>
                <button
                  onClick={() => onNavigate && onNavigate(currentIndex - 1)}
                  disabled={currentIndex === 0}
                  className="p-1.5 rounded-lg hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Previous product"
                  title="Previous (←)"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => onNavigate && onNavigate(currentIndex + 1)}
                  disabled={currentIndex === allResults.length - 1}
                  className="p-1.5 rounded-lg hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Next product"
                  title="Next (→)"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-base sm:text-lg font-bold text-foreground truncate">{product.name}</h1>
              {allResults.length > 1 && (
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {currentIndex + 1} of {allResults.length}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <Building2 className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
              <span className="text-xs sm:text-sm text-muted-foreground truncate">{vendor.name}</span>
              {vendorDetail?.city && (
                <span className="text-xs sm:text-sm text-muted-foreground/70">
                  {vendorDetail.city}{vendorDetail.state ? `, ${vendorDetail.state}` : ''}
                </span>
              )}
              {vendor.is_small_business && (
                <Badge variant="info" className="text-[10px]">Small Business</Badge>
              )}
              {vendor.is_8a_certified && (
                <Badge variant="info" className="text-[10px]">8(a)</Badge>
              )}
              {(allResults.length > 1 || programId) && (
                <span className="text-[10px] text-muted-foreground/50 ml-auto hidden sm:inline">
                  {allResults.length > 1 && '← →'}
                  {allResults.length > 1 && programId && ' · '}
                  {programId && 'S'}
                </span>
              )}
            </div>
          </div>

          <div
            data-testid="overall-badge"
            className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full flex-shrink-0 ${
              overallScore.isScored
                ? 'font-bold text-lg sm:text-xl'
                : 'text-xs sm:text-sm font-medium'
            } ${overallBadgeClasses(overallScore)}`}
          >
            {overallScore.text}
          </div>

          <Button
            variant={isShortlisted ? 'default' : 'outline'}
            size="sm"
            onClick={handleShortlistToggle}
            disabled={!programId || shortlistLoading}
            className="flex items-center gap-1.5 flex-shrink-0"
            title={programId ? "Toggle shortlist (S)" : "Shortlist unavailable"}
          >
            {isShortlisted ? (
              <BookmarkCheck className="w-4 h-4" />
            ) : (
              <Bookmark className="w-4 h-4" />
            )}
            <span className="hidden sm:inline">{shortlistLoading ? 'Saving...' : isShortlisted ? 'Shortlisted' : 'Add to Shortlist'}</span>
          </Button>
        </div>

        {/* Actions Bar - Always Visible */}
        <div className="px-4 sm:px-6 py-2 sm:py-3 border-b border-border flex flex-wrap gap-2 sm:gap-3 shrink-0">
          <Button variant="outline" size="sm" onClick={() => { setShowContactModal(true); contactTracking.onOpen() }} className="text-xs sm:text-sm">
            <User className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-1.5" /> <span className="hidden sm:inline">See Contact</span>
          </Button>
          <Button variant="outline" size="sm" onClick={() => { setShowRequestModal(true); requestInfoTracking.onOpen() }} className="text-xs sm:text-sm">
            <Send className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-1.5" /> <span className="hidden sm:inline">Request Info</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleRunFullEvaluation(true)}
            disabled={!programId || evaluationLoading || isFullyEvaluated}
            className="text-xs sm:text-sm"
            title={programId ? 'Run a full evaluation' : 'Full evaluation requires a program'}
            data-testid="run-full-evaluation"
          >
            <Play className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-1.5" />
            <span className="hidden sm:inline">{evaluationLoading ? 'Running...' :
             isFullyEvaluated ? 'Full Evaluation Complete' : 'Run Full Evaluation'}</span>
          </Button>
          {/* Surface the failure next to the button that caused it. The Analysis
              section further down is too far from the click to be noticed. */}
          {evaluationError && (
            <div
              className="flex items-center gap-1.5 text-xs sm:text-sm text-destructive"
              role="alert"
              data-testid="evaluation-error"
            >
              <AlertTriangle className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
              <span>{evaluationError}</span>
            </div>
          )}
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto pb-8">
        {/* Product Image + Description */}
        <div className="px-6 py-6 flex gap-6">
          {allImages.length > 0 && (
            <div
              className="w-48 h-32 rounded-lg overflow-hidden bg-muted flex-shrink-0 relative group"
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => setIsHovering(false)}
            >
              {/* Current image with crossfade */}
              {allImages.map((url, i) => (
                <img
                  key={url}
                  src={url}
                  alt={`${product.name} ${i + 1}`}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
                    i === currentImageIdx ? 'opacity-100' : 'opacity-0'
                  }`}
                />
              ))}

              {allImages.length > 1 && (
                <>
                  {/* Prev / Next buttons — visible on hover */}
                  <button
                    onClick={(e) => { e.stopPropagation(); goToPrev() }}
                    className={`absolute left-1 top-1/2 -translate-y-1/2 p-0.5 rounded-full
                               bg-black/40 text-white/80 hover:bg-black/60 hover:text-white
                               transition-opacity duration-200 ${isHovering ? 'opacity-100' : 'opacity-0'}`}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); goToNext() }}
                    className={`absolute right-1 top-1/2 -translate-y-1/2 p-0.5 rounded-full
                               bg-black/40 text-white/80 hover:bg-black/60 hover:text-white
                               transition-opacity duration-200 ${isHovering ? 'opacity-100' : 'opacity-0'}`}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>

                  {/* Pause/Play toggle — visible on hover */}
                  <button
                    onClick={(e) => { e.stopPropagation(); setAutoPlay(!autoPlay) }}
                    className={`absolute top-1 right-1 p-0.5 rounded-full
                               bg-black/40 text-white/80 hover:bg-black/60 hover:text-white
                               transition-opacity duration-200 ${isHovering ? 'opacity-100' : 'opacity-0'}`}
                  >
                    {autoPlay ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                  </button>

                  {/* Dot indicators */}
                  <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex gap-1">
                    {allImages.map((_, i) => (
                      <button
                        key={i}
                        onClick={(e) => { e.stopPropagation(); trackEvent('image_scrolled', { vendor_id: vendor.id, product_id: product.id, image_index: i, navigation_method: 'dot' }); setCurrentImageIdx(i) }}
                        className={`w-1.5 h-1.5 rounded-full transition-all ${
                          i === currentImageIdx
                            ? 'bg-white scale-110'
                            : 'bg-white/40 hover:bg-white/70'
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
          <div className="flex-1">
            <p className="text-sm text-foreground leading-relaxed">
              {product.description || 'No description available.'}
            </p>
            <div className="flex items-center gap-4 mt-3">
              {product.product_url && (
                <a
                  href={product.product_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-primary hover:underline flex items-center gap-1"
                >
                  Product page <ExternalLink className="w-3 h-3" />
                </a>
              )}
              {vendor.website && (
                <a
                  href={vendor.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-primary hover:underline flex items-center gap-1"
                >
                  Vendor website <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
            
            {/* Vendor Description */}
            {vendorDetail?.description && (
              <div className="mt-4 pt-4 border-t border-border">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                  About {vendor.name}
                </h3>
                <p className="text-sm text-foreground leading-relaxed">
                  {vendorDetail.description}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Score Summary */}
        <SectionHeader id="scores" icon={<Star className="w-4 h-4 text-primary" />} title="Score Summary" />
        {expandedSections.has('scores') && (
          <div className="px-6 py-4 border-b border-border">
            <div className="flex gap-8">
              {/* Radar chart */}
              <div className="flex-shrink-0">
                <ScoreRadarChart scores={scores} size={200} />
              </div>
              {/* Score bars — each expandable to show scorecard details */}
              <div className="flex-1 space-y-1">
                {[
                  { dimension: scores.need_fit, label: 'Basic Requirements', evalType: 'foundational' },
                  { dimension: scores.technical, label: 'Technical', evalType: 'technical' },
                  { dimension: scores.manufacturing, label: 'Production Readiness', evalType: 'manufacturing_readiness' },
                  { dimension: scores.vc_metrics, label: 'Company Stability', evalType: 'vc_metrics' },
                  { dimension: scores.dod_partnership, label: 'DoW Partnership', evalType: 'dod_partnership' },
                ].map(({ dimension, label, evalType }) => {
                  const evaluation = evalType
                    ? dedupedEvaluations.find(e => e.scorecard_type === evalType)
                    : null
                  const sectionKey = `score-${evalType || 'need_fit'}`
                  const isExpanded = expandedSections.has(sectionKey)
                  // Nothing to measure against on this record renders as its
                  // state, not as the placeholder number behind it (DEV-584).
                  const score = describeScore(dimension)

                  return (
                    <div key={label}>
                      <button
                        onClick={() => evalType && toggleSection(sectionKey)}
                        className={`w-full text-left space-y-1 px-3 py-2 rounded-lg transition-colors ${
                          evalType ? 'hover:bg-muted/30 cursor-pointer' : 'cursor-default'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5">
                            {evalType ? (
                              isExpanded
                                ? <ChevronDown className="w-3 h-3 text-muted-foreground" />
                                : <ChevronRight className="w-3 h-3 text-muted-foreground" />
                            ) : <div className="w-3" />}
                            <span className="text-xs font-medium text-foreground">{label}</span>
                            {evaluation && (
                              <Badge variant="info" className="text-[10px] ml-1">
                                {evaluation.items_with_data}/{evaluation.total_items} scored
                              </Badge>
                            )}
                            {evaluation?.evaluation_mode && evaluation.evaluation_mode !== 'fast' && (
                              <Badge variant="info" className="text-[10px]">
                                {evaluation.evaluation_mode}
                              </Badge>
                            )}
                          </div>
                          <span
                            className={`text-xs ${
                              score.isScored
                                ? `font-bold ${getScoreColor(score.value)}`
                                : 'text-muted-foreground'
                            }`}
                          >
                            {score.text}
                          </span>
                        </div>
                        {score.isScored ? (
                          <div className="h-2 bg-muted rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all ${
                                score.value >= 80 ? 'bg-success' :
                                score.value >= 60 ? 'bg-primary' :
                                score.value >= 40 ? 'bg-warning' :
                                'bg-destructive'
                              }`}
                              style={{ width: `${score.value}%` }}
                            />
                          </div>
                        ) : (
                          <div className="h-2 flex items-center">
                            <div className="w-full border-t border-dashed border-border" />
                          </div>
                        )}
                        <p className="text-[10px] text-muted-foreground">{score.explanation}</p>
                      </button>

                      {isExpanded && evaluation && (
                        <div className="ml-7 mr-3 mt-1 mb-2 space-y-3 border-l-2 border-border/40 pl-3">
                          {evaluation.section_evaluations.map((sectionEval) => (
                            <div key={sectionEval.id}>
                              <div className="flex items-center gap-2 mb-1.5">
                                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                                  {sectionEval.section.title}
                                </span>
                                <span className={`text-[10px] font-bold ${getScoreColor(sectionEval.score)}`}>
                                  {sectionEval.score}
                                </span>
                                <span className="text-[9px] text-muted-foreground/60">
                                  {sectionEval.items_with_data}/{sectionEval.total_items} with data
                                </span>
                                <div className="flex-1 h-px bg-border/40" />
                              </div>
                              <div className="space-y-1">
                                {sectionEval.item_evaluations.map((itemEval) => (
                                  <ItemEvalRow key={itemEval.id} item={itemEval} />
                                ))}
                              </div>
                            </div>
                          ))}
                          {evaluation.section_evaluations.length === 0 && (
                            <p className="text-[10px] text-muted-foreground italic py-2">
                              No detailed items available.
                            </p>
                          )}
                        </div>
                      )}

                      {isExpanded && evalType && !evaluation && (
                        <div className="ml-7 mr-3 mt-1 mb-2 pl-3 border-l-2 border-border/40">
                          <p className="text-[10px] text-muted-foreground italic py-2">
                            No evaluation data yet. Run a full evaluation to see details.
                          </p>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        )}


        {/* Qualitative Analysis */}
        {qualitativeObs.length > 0 && (
          <>
            <SectionHeader
              id="qualitative"
              icon={<Brain className="w-4 h-4 text-violet-400" />}
              title="Qualitative Analysis"
              badge={
                <Badge variant="info" className="text-[10px] ml-2">
                  {qualitativeObs.length} findings
                </Badge>
              }
            />
            {expandedSections.has('qualitative') && (
              <div className="px-6 py-4 border-b border-border space-y-1">
                {qualitativeObs.map((obs) => {
                  const isExpanded = expandedQualItems.has(obs.id)
                  return (
                    <div key={obs.id} className="rounded-lg border border-border/40 overflow-hidden">
                      <button
                        onClick={() => {
                          setExpandedQualItems(prev => {
                            const next = new Set(prev)
                            if (next.has(obs.id)) next.delete(obs.id)
                            else next.add(obs.id)
                            return next
                          })
                        }}
                        className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-muted/30 transition-colors text-left"
                      >
                        {isExpanded ? (
                          <ChevronDown className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                        ) : (
                          <ChevronRight className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                        )}
                        <span className="text-xs font-semibold text-foreground flex-1">{obs.spec_name}</span>
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                          obs.confidence >= 4 ? 'bg-emerald-400' :
                          obs.confidence >= 3 ? 'bg-amber-400' :
                          'bg-muted-foreground/40'
                        }`} title={`Confidence: ${obs.confidence}/5`} />
                      </button>
                      {isExpanded && (
                        <div className="px-4 pb-3 pt-0 ml-5.5">
                          <div className="prose prose-sm max-w-none prose-invert
                                          prose-headings:text-foreground prose-p:text-foreground/90
                                          prose-strong:text-foreground prose-em:text-foreground/80
                                          prose-ul:text-foreground/90 prose-ol:text-foreground/90
                                          prose-li:text-foreground/90 prose-code:text-foreground
                                          prose-pre:bg-muted prose-pre:text-foreground
                                          prose-a:text-primary hover:prose-a:text-primary/80">
                            <ReactMarkdown>{obs.value_text}</ReactMarkdown>
                          </div>
                          <div className="flex items-center gap-3 mt-2">
                            <span className="text-[9px] text-muted-foreground/50">
                              {obs.source_type === 'llm_extraction' ? 'AI Research' : obs.source_type}
                            </span>
                            <span className="text-[9px] text-muted-foreground/50">
                              {obs.observed_date}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </>
        )}

        {/* Vendor Contract Readiness */}
        <SectionHeader id="compliance" icon={<Shield className="w-4 h-4 text-green-400" />} title="Vendor Contract Readiness" />
        {expandedSections.has('compliance') && (
          <div className="px-6 py-4 border-b border-border space-y-4">
            {loadingDetail ? (
              <div className="animate-pulse space-y-2">
                <div className="h-4 bg-muted rounded w-1/3" />
                <div className="h-4 bg-muted rounded w-1/4" />
                <div className="h-4 bg-muted rounded w-1/2" />
              </div>
            ) : vendorDetailError ? (
              <p className="text-sm text-destructive" role="alert" data-testid="compliance-error">
                {vendorDetailError}
              </p>
            ) : vendorDetail && hasComplianceData(vendorDetail) ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <ComplianceItem
                  label="SAM.gov"
                  value={vendorDetail.sam_gov_id || vendorDetail.sam_status || 'Unknown'}
                  icon={vendorDetail.sam_gov_id ? <CheckCircle className="w-3.5 h-3.5 text-success" /> : <XCircle className="w-3.5 h-3.5 text-muted-foreground" />}
                  href={vendorDetail.uei ? `https://sam.gov/entity/${vendorDetail.uei}` : undefined}
                />
                <ComplianceItem
                  label="UEI"
                  value={vendorDetail.uei || 'N/A'}
                  icon={vendorDetail.uei ? <CheckCircle className="w-3.5 h-3.5 text-success" /> : <XCircle className="w-3.5 h-3.5 text-muted-foreground" />}
                  href={vendorDetail.uei ? `https://sam.gov/entity/${vendorDetail.uei}` : undefined}
                />
                <ComplianceItem
                  label="CMMC Level"
                  value={vendorDetail.cmmc_level || 'Not assessed'}
                />
                <ComplianceItem
                  label="CAGE Code"
                  value={vendorDetail.cage_code || 'N/A'}
                />
                {vendorDetail.iso_certifications && vendorDetail.iso_certifications.length > 0 && (
                  <ComplianceItem
                    label="ISO Certifications"
                    value={vendorDetail.iso_certifications.join(', ')}
                  />
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Compliance data not available.</p>
            )}

            <Button variant="outline" size="sm" onClick={() => { setShowContractsModal(true); contractsTracking.onOpen() }}>
              <FileText className="w-4 h-4 mr-1.5" /> View All Vendor Contracts
            </Button>
          </div>
        )}

        {/* Key Pros & Risks */}
        <SectionHeader id="pros_risks" icon={<AlertTriangle className="w-4 h-4 text-amber-400" />} title="Key Pros & Risks" />
        {expandedSections.has('pros_risks') && (
          <div className="px-6 py-4 border-b border-border">
            {(() => {
              // Find the most recent complete evaluation
              const completeEval = dedupedEvaluations.find(e => e.evaluation_mode === 'complete')
              
              // Show loading state
              if (evaluationLoading) {
                return (
                  <div className="flex flex-col items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-3"></div>
                    <p className="text-sm text-muted-foreground">Running full evaluation...</p>
                    <p className="text-xs text-muted-foreground mt-1">This may take 4-5 seconds</p>
                  </div>
                )
              }
              
              // Show error if any
              if (evaluationError) {
                return (
                  <div className="text-center py-4">
                    <p className="text-sm text-destructive mb-2">{evaluationError}</p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-xs"
                      onClick={() => handleRunFullEvaluation(true)}
                    >
                      <Play className="w-3 h-3 mr-1" /> Try Again
                    </Button>
                  </div>
                )
              }
              
              // Show structured data if complete evaluation exists
              if (completeEval && (completeEval.key_strengths?.length || completeEval.major_gaps?.length || completeEval.overall_assessment)) {
                return (
                  <div>
                    {/* Overall Assessment */}
                    {completeEval.overall_assessment && (
                      <div className="mb-4 p-3 bg-muted/30 rounded-lg">
                        <p className="text-xs text-foreground leading-relaxed">
                          {completeEval.overall_assessment}
                        </p>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-2 gap-6">
                      {/* Key Strengths */}
                      <div>
                        <h4 className="text-xs font-semibold text-success mb-2 flex items-center gap-1">
                          <CheckCircle className="w-3.5 h-3.5" /> Key Strengths
                        </h4>
                        {completeEval.key_strengths && completeEval.key_strengths.length > 0 ? (
                          <ul className="space-y-1.5">
                            {completeEval.key_strengths.map((strength: string, i: number) => (
                              <li key={i} className="text-xs text-foreground flex items-start gap-1.5">
                                <span className="text-success mt-0.5 font-bold">+</span>
                                <span>{strength}</span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-xs text-muted-foreground italic">No strengths identified</p>
                        )}
                      </div>
                      
                      {/* Major Gaps */}
                      <div>
                        <h4 className="text-xs font-semibold text-destructive mb-2 flex items-center gap-1">
                          <AlertTriangle className="w-3.5 h-3.5" /> Major Gaps
                        </h4>
                        {completeEval.major_gaps && completeEval.major_gaps.length > 0 ? (
                          <ul className="space-y-1.5">
                            {completeEval.major_gaps.map((gap: string, i: number) => (
                              <li key={i} className="text-xs text-foreground flex items-start gap-1.5">
                                <span className="text-destructive mt-0.5 font-bold">−</span>
                                <span>{gap}</span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-xs text-muted-foreground italic">No major gaps identified</p>
                        )}
                      </div>
                    </div>
                  </div>
                )
              }
              
              // No complete evaluation yet — auto-evaluation is in progress
              return (
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-3"></div>
                  <p className="text-sm text-muted-foreground">Running full evaluation...</p>
                  <p className="text-xs text-muted-foreground mt-1">This may take a few seconds</p>
                </div>
              )
            })()}
          </div>
        )}

      </div>
        </div>
      </div>
      
      {/* Modals */}
      <ContactInfoModal
        isOpen={showContactModal}
        onClose={() => { setShowContactModal(false); contactTracking.onClose() }}
        vendor={vendor}
        teamMembers={vendorDetail?.team_members || []}
      />

      <RequestInfoModal
        isOpen={showRequestModal}
        onClose={() => { setShowRequestModal(false); requestInfoTracking.onClose() }}
        vendor={vendor}
        vendorDetail={vendorDetail}
        programId={programId}
        programName={programName}
        customRequest={customInfoRequest}
        setCustomRequest={setCustomInfoRequest}
      />

      {/* Vendor Contracts Modal */}
      <VendorContractsModal
        isOpen={showContractsModal}
        onClose={() => { setShowContractsModal(false); contractsTracking.onClose() }}
        vendorId={vendor.id}
        vendorName={vendor.name}
      />
    </div>
  )

  return createPortal(overlayContent, document.body)
}

// ── Sub-components ─────────────────────────────────────────────────

// Contact Info Modal
const ContactInfoModal: React.FC<{
  isOpen: boolean
  onClose: () => void
  vendor: { id: number; name: string }
  teamMembers: TeamMember[]
}> = ({ isOpen, onClose, vendor: _vendor, teamMembers }) => {
  if (!isOpen) return null
  
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Contact Information" size="md">
      <div className="space-y-4">
        {teamMembers.length > 0 ? (
          teamMembers.map(member => (
            <div key={member.id} className="border border-border rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-semibold text-foreground">{member.name}</h4>
                  <p className="text-sm text-muted-foreground">
                    {member.title || member.role}
                  </p>
                </div>
                {member.is_c_suite && (
                  <Badge variant="info" className="text-xs">C-Suite</Badge>
                )}
                {member.is_founder && (
                  <Badge variant="info" className="text-xs ml-1">Founder</Badge>
                )}
              </div>
              
              <div className="mt-3 space-y-2">
                {member.email && (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <a href={`mailto:${member.email}`} className="text-primary hover:underline">
                      {member.email}
                    </a>
                  </div>
                )}
                {member.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <span className="text-foreground">{member.phone}</span>
                  </div>
                )}
                {member.linkedin_url && (
                  <div className="flex items-center gap-2 text-sm">
                    <ExternalLink className="w-4 h-4 text-muted-foreground" />
                    <a href={member.linkedin_url} target="_blank" rel="noopener noreferrer" 
                       className="text-primary hover:underline">
                      LinkedIn Profile
                    </a>
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <p className="text-sm text-muted-foreground text-center py-4">
            No team member information available yet.
          </p>
        )}
      </div>
    </Modal>
  )
}

// Request Info Modal
const RequestInfoModal: React.FC<{
  isOpen: boolean
  onClose: () => void
  vendor: { id: number; name: string }
  vendorDetail: VendorDetailData | null
  programId?: string
  programName: string
  customRequest: string
  setCustomRequest: (val: string) => void
}> = ({ isOpen, onClose, vendor, vendorDetail, programId, programName, customRequest, setCustomRequest }) => {
  const [copiedUrl, setCopiedUrl] = useState(false)
  const [copiedEmail, setCopiedEmail] = useState(false)
  
  const submissionUrl = programId 
    ? `${window.location.origin}/submit/new/${programId}` 
    : ''
  
  const handleCopyUrl = () => {
    navigator.clipboard.writeText(submissionUrl)
    setCopiedUrl(true)
    setTimeout(() => setCopiedUrl(false), 2000)
  }
  
  const handleCopyEmail = () => {
    if (vendorDetail?.primary_contact_email) {
      navigator.clipboard.writeText(vendorDetail.primary_contact_email)
      setCopiedEmail(true)
      setTimeout(() => setCopiedEmail(false), 2000)
    }
  }
  
  if (!isOpen) return null
  
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Request Information" size="lg">
      <div className="space-y-6">
        <div className="bg-muted/30 rounded-lg p-4">
          <p className="text-sm text-foreground leading-relaxed">
            <strong>{vendor.name}</strong> can submit additional details to the following link:
          </p>
          
          {submissionUrl && (
            <div className="mt-3 flex items-center gap-2">
              <input 
                type="text" 
                value={submissionUrl}
                readOnly
                className="flex-1 px-3 py-2 text-sm bg-background border border-border rounded-md"
              />
              <Button variant="outline" size="sm" onClick={handleCopyUrl}>
                {copiedUrl ? <CheckCircle className="w-4 h-4 mr-1.5" /> : <Copy className="w-4 h-4 mr-1.5" />}
                {copiedUrl ? 'Copied!' : 'Copy'}
              </Button>
            </div>
          )}
          
          <p className="text-xs text-muted-foreground mt-3">
            This link automatically checks that <strong>{vendor.name}</strong>'s submission 
            addresses all of the technical and manufacturing requirements that you've already 
            specified for <strong>{programName || 'this program'}</strong>.
          </p>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Request Specific Information (optional)
          </label>
          <p className="text-xs text-muted-foreground mb-3">
            Make a brief note about what you'd like to see, and we'll include it in the submission evaluation.
          </p>
          <textarea
            value={customRequest}
            onChange={(e) => setCustomRequest(e.target.value)}
            placeholder="e.g., Please provide detailed manufacturing timeline and capacity information..."
            className="w-full h-32 px-3 py-2 text-sm bg-background border border-border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        
        <div className="flex items-center gap-3">
          {vendorDetail?.primary_contact_email && (
            <Button variant="outline" onClick={handleCopyEmail}>
              {copiedEmail ? <CheckCircle className="w-4 h-4 mr-1.5" /> : <Mail className="w-4 h-4 mr-1.5" />}
              {copiedEmail ? 'Copied!' : `Copy Email: ${vendorDetail.primary_contact_email}`}
            </Button>
          )}
          <Button variant="default">
            <Send className="w-4 h-4 mr-1.5" />
            Have Overmatch Send Info Request
          </Button>
        </div>
      </div>
    </Modal>
  )
}

const ComplianceItem: React.FC<{
  label: string
  value: string
  icon?: React.ReactNode
  href?: string
}> = ({ label, value, icon, href }) => (
  <div className="space-y-0.5">
    <span className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</span>
    <div className="flex items-center gap-1.5">
      {icon}
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-primary hover:text-primary/80 hover:underline flex items-center gap-1"
        >
          {value}
          <ExternalLink className="w-3 h-3" />
        </a>
      ) : (
        <span className="text-xs text-foreground">{value}</span>
      )}
    </div>
  </div>
)

// ── Item-level evaluation row ────────────────────────────────────

const ItemEvalRow: React.FC<{ item: ItemEvaluation }> = ({ item }) => {
  const hasData = item.has_data && item.observed_value != null
  const score = item.score
  const criteria = item.acceptance_criteria
  const observed = item.observed_value

  // Score color
  const scoreColor = hasData
    ? score >= 80
      ? 'text-emerald-400'
      : score >= 60
      ? 'text-primary'
      : score >= 40
      ? 'text-amber-400'
      : 'text-red-400'
    : 'text-muted-foreground/40'

  // Background for score pill
  const scoreBg = hasData
    ? score >= 80
      ? 'bg-emerald-400/10'
      : score >= 60
      ? 'bg-primary/10'
      : score >= 40
      ? 'bg-amber-400/10'
      : 'bg-red-400/10'
    : 'bg-muted/30'

  // source_url is research-sourced (LLM/Perplexity) and therefore untrusted —
  // only treat it as a clickable link when it's a real http(s) URL, otherwise
  // a javascript:/data: scheme could fire on click. null = not safe to link.
  const safeSourceUrl = (() => {
    const raw = observed?.source_url
    if (!raw) return null
    try {
      const u = new URL(raw)
      return u.protocol === 'http:' || u.protocol === 'https:' ? u.toString() : null
    } catch {
      return null
    }
  })()

  // Format observed value
  const formatObserved = () => {
    if (!observed) return null
    if (observed.type === 'numeric' && typeof observed.value === 'number') {
      return `${observed.value}${observed.unit ? ` ${observed.unit}` : ''}`
    }
    if (observed.type === 'boolean') {
      return observed.value ? 'Yes' : 'No'
    }
    return String(observed.value)
  }

  // Build threshold display for numeric
  const formatThresholds = () => {
    if (!criteria || criteria.type !== 'numeric') return null
    const parts: string[] = []
    const higher = criteria.higher_is_better ?? true
    if (higher) {
      if (criteria.lower_threshold != null) parts.push(`fail < ${criteria.lower_threshold}`)
      if (criteria.upper_threshold != null) parts.push(`good >= ${criteria.upper_threshold}`)
    } else {
      if (criteria.lower_threshold != null) parts.push(`good <= ${criteria.lower_threshold}`)
      if (criteria.upper_threshold != null) parts.push(`fail > ${criteria.upper_threshold}`)
    }
    return parts.join(' · ')
  }

  return (
    <div
      className={`flex items-start gap-3 px-3 py-2 rounded-lg transition-colors ${
        hasData ? 'bg-card/60' : 'bg-muted/10 opacity-60'
      }`}
    >
      {/* Score pill */}
      <div className={`w-10 h-7 rounded-md flex items-center justify-center text-xs font-bold flex-shrink-0 ${scoreBg} ${scoreColor}`}>
        {hasData ? score : '—'}
      </div>

      {/* Item info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-xs font-medium ${hasData ? 'text-foreground' : 'text-muted-foreground'}`}>
            {item.item_title}
          </span>
          {item.item_details?.importance && (
            <span className={`text-[9px] px-1.5 py-0 rounded-full ${
              item.item_details.importance === 'Mission Critical'
                ? 'bg-destructive/10 text-destructive'
                : item.item_details.importance === 'Important'
                ? 'bg-primary/10 text-primary'
                : 'bg-muted text-muted-foreground'
            }`}>
              {item.item_details.importance}
            </span>
          )}
          {/* Confidence indicator */}
          {hasData && observed?.confidence && (
            <span className="flex items-center gap-1">
              <span className={`w-1.5 h-1.5 rounded-full ${
                observed.confidence === 'high'
                  ? 'bg-emerald-400'
                  : observed.confidence === 'medium'
                  ? 'bg-amber-400'
                  : 'bg-muted-foreground/40'
              }`} />
              <span className="text-[9px] text-muted-foreground/60">{observed.confidence} confidence</span>
            </span>
          )}
        </div>

        {/* Observed value + thresholds */}
        {hasData && observed ? (
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[10px] text-foreground font-medium">{formatObserved()}</span>
            {formatThresholds() && (
              <span className="text-[9px] text-muted-foreground/60">{formatThresholds()}</span>
            )}
          </div>
        ) : (
          <span className="text-[10px] text-muted-foreground/50 italic mt-0.5 block">
            No data available
          </span>
        )}

        {/* Scoring rationale — how this item ranked against the thresholds */}
        {hasData && item.rationale && (
          <p className="text-[10px] text-muted-foreground leading-relaxed mt-1 whitespace-pre-wrap">
            {item.rationale}
          </p>
        )}

        {/* Research commentary — what the deep/fast research actually found and
            why this value was determined (Observation.commentary). */}
        {hasData && observed?.commentary && (
          <p className="text-[10px] text-muted-foreground/80 leading-relaxed mt-1 whitespace-pre-wrap border-l-2 border-border pl-2">
            {observed.commentary}
          </p>
        )}

        {/* Source link for the observation, when research captured a safe
            http(s) URL. Non-http(s) schemes render as plain text, never a link. */}
        {hasData && safeSourceUrl ? (
          <a
            href={safeSourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[9px] text-primary/80 hover:text-primary hover:underline mt-1 inline-block truncate max-w-full"
          >
            {safeSourceUrl}
          </a>
        ) : hasData && observed?.source_url ? (
          <span className="text-[9px] text-muted-foreground/60 mt-1 inline-block truncate max-w-full">
            {observed.source_url}
          </span>
        ) : null}
      </div>
    </div>
  )
}

export default VendorDetailOverlay
